import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Link,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Instagram,
  CheckCircle,
  Error,
  Info,
  Visibility,
  VisibilityOff,
  ExpandMore,
  Launch,
  ContentCopy
} from '@mui/icons-material';
import api from '../services/api';

interface InstagramChannelData {
  name: string;
  provider: string;
  instagram_id?: string;
  username?: string;
  provider_config: Record<string, any>;
  webhook_verify_token?: string;
  greeting_enabled: boolean;
  greeting_message: string;
  auto_assignment_enabled: boolean;
  sync_messages: boolean;
  auto_response_enabled: boolean;
}

interface Provider {
  name: string;
  description: string;
  features: string[];
  setup_complexity: string;
  documentation_url: string;
  limitations?: string;
}

interface ConfigField {
  type: string;
  label: string;
  required: boolean;
  default?: any;
  description?: string;
  options?: { value: string; label: string; }[];
}

const steps = [
  'Provider Selection',
  'Provider Configuration', 
  'Channel Settings',
  'Verification'
];

interface InstagramChannelConfigProps {
  editingChannel?: any;
  onCancel: () => void;
}

const InstagramChannelConfig: React.FC<InstagramChannelConfigProps> = ({
  editingChannel,
  onCancel
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});

  const [providers, setProviders] = useState<Record<string, Provider>>({});
  const [configFields, setConfigFields] = useState<Record<string, ConfigField>>({});
  const [createdChannel, setCreatedChannel] = useState<any>(null);
  const [verificationResult, setVerificationResult] = useState<any>(null);

  const [formData, setFormData] = useState<InstagramChannelData>({
    name: '',
    provider: 'meta',
    provider_config: {},
    greeting_enabled: true,
    greeting_message: 'Hello! Welcome to our Instagram support. How can we help you today?',
    auto_assignment_enabled: true,
    sync_messages: true,
    auto_response_enabled: false,
  });

  useEffect(() => {
    loadProviders();
    if (editingChannel) {
      loadChannelData();
    }
  }, [editingChannel]);

  useEffect(() => {
    if (formData.provider) {
      loadProviderConfig(formData.provider);
    }
  }, [formData.provider]);

  const loadProviders = async () => {
    try {
      const response = await api.get('/instagram/providers');
      setProviders(response.data);
    } catch (error) {
      console.error('Failed to load Instagram providers:', error);
      setError('Failed to load providers');
    }
  };

  const loadProviderConfig = async (provider: string) => {
    try {
      const response = await api.get(`/instagram/providers/${provider}/config`);
      setConfigFields(response.data.config_fields);
    } catch (error) {
      console.error('Failed to load provider config:', error);
      setError('Failed to load provider configuration');
    }
  };

  const loadChannelData = async () => {
    if (!editingChannel) return;

    try {
      const response = await api.get(`/instagram/channels/${editingChannel.id}`);
      const channelData = response.data;

      setFormData({
        name: editingChannel.name,
        provider: channelData.provider || 'meta',
        provider_config: channelData.provider_config || {},
        greeting_enabled: editingChannel.greeting_enabled,
        greeting_message: editingChannel.greeting_message || 'Hello! Welcome to our Instagram support. How can we help you today?',
        auto_assignment_enabled: editingChannel.auto_assignment_enabled,
        sync_messages: channelData.sync_messages ?? true,
        auto_response_enabled: channelData.auto_response_enabled ?? false,
      });

      setCreatedChannel(channelData);
    } catch (error) {
      console.error('Failed to load channel data:', error);
      setError('Failed to load channel data');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('provider_config.')) {
      const configField = field.replace('provider_config.', '');
      setFormData(prev => ({
        ...prev,
        provider_config: {
          ...prev.provider_config,
          [configField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Provider Selection
        return !!formData.provider;
      case 1: // Provider Configuration
        return Object.entries(configFields).every(([key, config]) => {
          if (!config.required) return true;
          const value = formData.provider_config[key];
          return value !== undefined && value !== null && value !== '';
        });
      case 2: // Channel Settings
        return !!formData.name && !!formData.greeting_message;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
      setError(null);
    } else {
      setError('Please fill in all required fields before proceeding.');
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingChannel) {
        const response = await api.put(`/instagram/channels/${editingChannel.id}`, formData);
        setSuccess('Instagram channel updated successfully!');
        setTimeout(() => {
          onCancel(); // Close dialog after successful update
        }, 1500);
      } else {
        const response = await api.post('/instagram/channels', formData);
        setCreatedChannel(response.data);
        setSuccess('Instagram channel created successfully!');
        setActiveStep(3);
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || `Failed to ${editingChannel ? 'update' : 'create'} Instagram channel`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!createdChannel) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/instagram/channels/${createdChannel.id}/verify`);
      setVerificationResult(response.data);
      if (response.data.verified) {
        setSuccess('Instagram channel verified successfully!');
      } else {
        setError(response.data.error || 'Verification failed');
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const renderProviderSelection = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Instagram Provider
      </Typography>
      <Grid container spacing={2}>
        {Object.entries(providers).map(([key, provider]) => (
          <Grid item xs={12} md={6} key={key}>
            <Card 
              variant={formData.provider === key ? 'elevation' : 'outlined'}
              sx={{ 
                cursor: 'pointer',
                border: formData.provider === key ? '2px solid #1976d2' : undefined
              }}
              onClick={() => handleInputChange('provider', key)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <Instagram sx={{ mr: 1 }} />
                  <Typography variant="h6">{provider.name}</Typography>
                </Box>
                <Typography color="text.secondary" paragraph>
                  {provider.description}
                </Typography>
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Features:</Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {provider.features.map((feature, index) => (
                      <Chip key={index} label={feature} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Setup: {provider.setup_complexity}
                </Typography>
                {provider.limitations && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {provider.limitations}
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Launch />}
                  sx={{ mt: 1 }}
                  href={provider.documentation_url}
                  target="_blank"
                >
                  Documentation
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderConfigField = (key: string, config: ConfigField) => {
    const value = formData.provider_config[key] || config.default || '';
    
    if (config.type === 'select') {
      return (
        <FormControl fullWidth key={key}>
          <InputLabel>{config.label}</InputLabel>
          <Select
            value={value}
            label={config.label}
            onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
            required={config.required}
          >
            {config.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (config.type === 'boolean') {
      return (
        <FormControlLabel
          key={key}
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.checked)}
            />
          }
          label={config.label}
        />
      );
    }

    if (config.type === 'textarea') {
      return (
        <TextField
          key={key}
          label={config.label}
          value={value}
          onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
          required={config.required}
          multiline
          rows={3}
          fullWidth
          helperText={config.description}
        />
      );
    }

    return (
      <TextField
        key={key}
        label={config.label}
        value={value}
        onChange={(e) => handleInputChange(`provider_config.${key}`, e.target.value)}
        required={config.required}
        fullWidth
        type={config.type === 'password' ? (showPasswords[key] ? 'text' : 'password') : config.type}
        helperText={config.description}
        InputProps={config.type === 'password' ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => togglePasswordVisibility(key)} edge="end">
                {showPasswords[key] ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          )
        } : undefined}
      />
    );
  };

  const renderProviderConfiguration = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Configure {providers[formData.provider]?.name || 'Instagram'} Provider
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Please configure your Instagram provider settings. All required fields must be filled.
        </Typography>
      </Alert>
      
      <Grid container spacing={2}>
        {Object.entries(configFields).map(([key, config]) => (
          <Grid item xs={12} key={key}>
            {renderConfigField(key, config)}
          </Grid>
        ))}
      </Grid>

      {providers[formData.provider]?.documentation_url && (
        <Box mt={2}>
          <Button
            variant="outlined"
            startIcon={<Launch />}
            href={providers[formData.provider].documentation_url}
            target="_blank"
          >
            Setup Documentation
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderChannelSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Channel Settings
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Channel Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
            fullWidth
            helperText="A descriptive name for your Instagram channel"
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            label="Instagram Username"
            value={formData.username || ''}
            onChange={(e) => handleInputChange('username', e.target.value)}
            fullWidth
            helperText="Your Instagram username (optional)"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.greeting_enabled}
                onChange={(e) => handleInputChange('greeting_enabled', e.target.checked)}
              />
            }
            label="Enable Greeting Message"
          />
        </Grid>

        {formData.greeting_enabled && (
          <Grid item xs={12}>
            <TextField
              label="Greeting Message"
              value={formData.greeting_message}
              onChange={(e) => handleInputChange('greeting_message', e.target.value)}
              required
              multiline
              rows={3}
              fullWidth
              helperText="Message sent when a conversation starts"
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.sync_messages}
                onChange={(e) => handleInputChange('sync_messages', e.target.checked)}
              />
            }
            label="Sync Messages"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_assignment_enabled}
                onChange={(e) => handleInputChange('auto_assignment_enabled', e.target.checked)}
              />
            }
            label="Auto-assign Conversations"
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_response_enabled}
                onChange={(e) => handleInputChange('auto_response_enabled', e.target.checked)}
              />
            }
            label="Enable Auto-Response"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderVerification = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Instagram Channel Created
      </Typography>
      
      {createdChannel && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {createdChannel.name}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Provider:</Typography>
                <Typography>{createdChannel.provider}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Status:</Typography>
                <Box display="flex" alignItems="center">
                  {createdChannel.is_verified ? (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  ) : (
                    <Error color="warning" sx={{ mr: 1 }} />
                  )}
                  <Typography>
                    {createdChannel.is_verified ? 'Verified' : 'Not Verified'}
                  </Typography>
                </Box>
              </Grid>
              {createdChannel.webhook_url && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Webhook URL:</Typography>
                  <Box display="flex" alignItems="center">
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace',
                        backgroundColor: 'grey.100',
                        padding: '4px 8px',
                        borderRadius: 1,
                        flexGrow: 1,
                        mr: 1
                      }}
                    >
                      {createdChannel.webhook_url}
                    </Typography>
                    <IconButton 
                      size="small" 
                      onClick={() => copyToClipboard(createdChannel.webhook_url)}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Button
        variant="contained"
        onClick={handleVerify}
        disabled={loading || !createdChannel}
        startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
        fullWidth
      >
        {loading ? 'Verifying...' : 'Verify Channel'}
      </Button>

      {verificationResult && (
        <Alert 
          severity={verificationResult.verified ? 'success' : 'error'} 
          sx={{ mt: 2 }}
        >
          {verificationResult.verified ? (
            'Instagram channel verified successfully!'
          ) : (
            `Verification failed: ${verificationResult.error}`
          )}
        </Alert>
      )}
    </Box>
  );

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Instagram sx={{ mr: 2 }} />
        <Typography variant="h5">
          {editingChannel ? 'Edit' : 'Setup'} Instagram Channel
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
            <StepContent>
              {index === 0 && renderProviderSelection()}
              {index === 1 && renderProviderConfiguration()}
              {index === 2 && renderChannelSettings()}
              {index === 3 && renderVerification()}
              
              <Box sx={{ mb: 2, mt: 2 }}>
                <div>
                  {index < 3 && (
                    <Button
                      variant="contained"
                      onClick={index === 2 ? handleSubmit : handleNext}
                      disabled={loading || !validateStep(index)}
                      sx={{ mt: 1, mr: 1 }}
                      startIcon={loading ? <CircularProgress size={20} /> : undefined}
                    >
                      {loading ? 'Processing...' : (index === 2 ? 'Create Channel' : 'Continue')}
                    </Button>
                  )}
                  {index > 0 && index < 3 && (
                    <Button
                      disabled={loading}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </Button>
                  )}
                </div>
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Paper>
  );
};

export default InstagramChannelConfig;